(() => {
  const SECTION_SELECTOR = '[data-section-type="collection-template"]';

  class CollectionFacetsController {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.abortController = null;
      this.loadMoreAbortController = null;
      this.isLoading = false;
      this.savedScrollPosition = null;

      this.handleSectionChange = this.handleSectionChange.bind(this);
      this.handleSectionSubmit = this.handleSectionSubmit.bind(this);
      this.handleSectionClick = this.handleSectionClick.bind(this);
      this.handleSectionKeydown = this.handleSectionKeydown.bind(this);
      this.handleEscape = this.handleEscape.bind(this);
      this.handlePopState = this.handlePopState.bind(this);

      this.section.addEventListener('change', this.handleSectionChange);
      this.section.addEventListener('submit', this.handleSectionSubmit);
      this.section.addEventListener('click', this.handleSectionClick);
      this.section.addEventListener('keydown', this.handleSectionKeydown);
      document.addEventListener('keyup', this.handleEscape);
      window.addEventListener('popstate', this.handlePopState);

      this.updateUiState();
    }

    get facetsWrapper() {
      return this.section.querySelector('[data-facets-form]');
    }

    get filtersForm() {
      return this.section.querySelector('[data-facets-filters]');
    }

    get sortInput() {
      return this.section.querySelector('[data-sort-input]');
    }

    get sortOptions() {
      return this.section.querySelectorAll('[data-sort-option]');
    }

    get sortSelect() {
      return this.section.querySelector('[data-sort-select]');
    }

    get productsContainer() {
      return this.section.querySelector('[data-products-container]');
    }

    get productGrid() {
      return this.section.querySelector('[data-product-grid]');
    }

    get productList() {
      return this.section.querySelector('[data-product-list]');
    }

    get loadingOverlay() {
      return this.section.querySelector('[data-loading]');
    }

    get clearButton() {
      return this.section.querySelector('[data-clear-filters]');
    }

    get loadMoreButton() {
      return this.section.querySelector('[data-load-more]');
    }

    get loadMoreContainer() {
      return this.section.querySelector('[data-load-more-container]');
    }

    get productCountElement() {
      return this.section.querySelector('#ProductCount');
    }

    get activeCountBadge() {
      return this.section.querySelector('[data-facet-count]');
    }

    get collectionUrl() {
      const wrapper = this.facetsWrapper;
      return wrapper ? wrapper.dataset.collectionUrl || window.location.pathname : window.location.pathname;
    }

    set collectionUrl(value) {
      if (this.facetsWrapper) {
        this.facetsWrapper.dataset.collectionUrl = value;
      }
    }

    handleSectionChange(event) {
      const target = event.target;
      if (target.matches('[data-facet-input]')) {
        event.preventDefault();
        this.submitFacetForms();
        return;
      }

      if (target.matches('[data-sort-select]')) {
        event.preventDefault();
        this.handleSortSelection(null, target.value);
      }
    }

    handleSectionSubmit(event) {
      const form = event.target;
      if (form.matches('[data-facets-filters]')) {
        event.preventDefault();
        this.submitFacetForms();
      }
    }

    handleSectionKeydown(event) {
      if (event.key !== 'Enter') return;

      const target = event.target;
      if (!target.matches('[data-facet-input][type="checkbox"]') || target.disabled) return;

      event.preventDefault();
      target.checked = !target.checked;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleSectionClick(event) {
      const toggleButton = event.target.closest('[data-facets-open]');
      if (toggleButton) {
        event.preventDefault();
        this.openDialog();
        return;
      }

      const closeButton = event.target.closest('[data-facets-close]');
      if (closeButton) {
        event.preventDefault();
        this.closeDialog();
        return;
      }

      const sortOption = event.target.closest('[data-sort-option]');
      if (sortOption) {
        event.preventDefault();
        this.handleSortSelection(sortOption);
        return;
      }

      const removeLink = event.target.closest('[data-facet-remove]');
      if (removeLink) {
        event.preventDefault();
        this.renderFromUrl(removeLink.getAttribute('href'));
        return;
      }

      const clearControl = event.target.closest('[data-facets-clear]');
      if (clearControl) {
        event.preventDefault();
        this.closeDialog();
        const clearUrl =
          clearControl.getAttribute('href') ||
          clearControl.dataset.clearUrl ||
          this.collectionUrl;
        this.renderFromUrl(clearUrl, { keepDrawerOpen: false });
        return;
      }

      const clearButton = event.target.closest('[data-clear-filters]');
      if (clearButton) {
        event.preventDefault();
        const url = clearButton.dataset.clearUrl || this.collectionUrl || window.location.pathname;
        this.renderFromUrl(url);
        return;
      }

      const loadMoreTrigger = event.target.closest('[data-load-more]');
      if (loadMoreTrigger) {
        event.preventDefault();
        this.loadMoreProducts(loadMoreTrigger);
        return;
      }
    }

    handleEscape(event) {
      if (event.code !== 'Escape') return;
      const dialog = this.section.querySelector('[data-facets-drawer]');
      if (dialog && dialog.getAttribute('aria-hidden') === 'false') {
        this.closeDialog();
      }
    }

    handlePopState() {
      this.renderFromUrl(window.location.href, { updateHistory: false });
    }

    submitFacetForms() {
      const filterForm = this.filtersForm;
      const formData = filterForm ? new FormData(filterForm) : new FormData();

      const baseUrl = this.collectionUrl || window.location.pathname;
      const url = new URL(baseUrl, window.location.origin);
      const searchParams = new URLSearchParams(formData);

      // Clean empty values to avoid invalid params
      searchParams.forEach((value, key) => {
        if (value === '') {
          searchParams.delete(key);
        }
      });

      const sortInput = this.sortInput;
      if (sortInput && sortInput.value) {
        searchParams.set('sort_by', sortInput.value);
      } else if (!sortInput) {
        searchParams.delete('sort_by');
      }

      url.search = searchParams.toString();
      const keepDrawerOpen = this.isDrawerOpen() && !this.shouldAutoCloseFacets();
      this.renderFromUrl(url.toString(), { keepDrawerOpen });
    }

    renderFromUrl(url, options = {}) {
      if (!url) return;
      const { updateHistory = true, keepDrawerOpen: keepDrawerOpenOption } = options;
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const shouldAutoClose = this.shouldAutoCloseFacets();
      const keepDrawerOpen =
        keepDrawerOpenOption !== undefined
          ? keepDrawerOpenOption
          : !shouldAutoClose && this.isDrawerOpen();

      this.setLoadingState(true);
      if (shouldAutoClose) {
        this.closeDialog();
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      fetch(fetchUrl.toString(), { signal: this.abortController.signal })
        .then(response => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then(html => {
          this.renderResponse(html, { keepDrawerOpen });
          if (updateHistory) {
            const cleanUrl = new URL(fetchUrl.toString());
            cleanUrl.searchParams.delete('section_id');
            window.history.pushState({ sectionId: this.sectionId }, '', cleanUrl.pathname + cleanUrl.search);
            this.collectionUrl = cleanUrl.pathname;
          } else {
            const cleanUrl = new URL(fetchUrl.toString());
            cleanUrl.searchParams.delete('section_id');
            this.collectionUrl = cleanUrl.pathname;
          }
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error('Collection facets error', error);
        })
        .finally(() => {
          this.setLoadingState(false);
        });
    }

    renderResponse(htmlString, options = {}) {
      if (!htmlString) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const newSection = doc.querySelector(`${SECTION_SELECTOR}[data-section-id="${this.sectionId}"]`);
      if (!newSection) return;

      const newFacets = newSection.querySelector('[data-facets-form]');
      const newProducts = newSection.querySelector('[data-products-container]');

      const previouslyOpenFacets = this.getOpenFacetIds();
      const preserveDrawer = options.keepDrawerOpen && !this.shouldAutoCloseFacets();

      if (newFacets && this.facetsWrapper) {
        if (preserveDrawer && this.updateFacetsPreservingDrawer(newFacets)) {
          this.syncFacetDrawerState(previouslyOpenFacets, { skipAnimation: true });
        } else {
          this.facetsWrapper.innerHTML = newFacets.innerHTML;
          this.facetsWrapper.dataset.collectionUrl = newFacets.dataset.collectionUrl || this.collectionUrl;
          this.syncFacetDrawerState(previouslyOpenFacets);

          if (preserveDrawer) {
            this.restoreDrawerOpenState();
          }
        }
      }

      if (newProducts && this.productsContainer) {
        this.productsContainer.innerHTML = newProducts.innerHTML;
        if (newProducts.dataset.nextUrl !== undefined) {
          this.productsContainer.dataset.nextUrl = newProducts.dataset.nextUrl;
        } else {
          delete this.productsContainer.dataset.nextUrl;
        }
        window.theme?.reinitImageAnimation?.();
      }

      this.syncProductCount(newSection);
      this.updateUiState();
    }

    updateFacetsPreservingDrawer(newFacets) {
      const wrapper = this.facetsWrapper;
      if (!wrapper || !newFacets) return false;

      const currentInner = wrapper.querySelector('.collection-facets__inner');
      const newInner = newFacets.querySelector('.collection-facets__inner');
      if (!currentInner || !newInner) return false;

      const currentDialog = currentInner.querySelector('[data-facets-drawer]');
      if (!currentDialog || !this.isDrawerOpen()) return false;

      const currentSummary = currentInner.querySelector('.collection-facets__summary');
      const newSummary = newInner.querySelector('.collection-facets__summary');
      if (currentSummary && newSummary) {
        currentSummary.innerHTML = newSummary.innerHTML;
      }

      this.syncActiveFacetsBar(currentInner, newInner);

      const newDialog = newInner.querySelector('[data-facets-drawer]');
      const currentBody = currentDialog.querySelector('.collection-facets__dialog-body');
      const newBody = newDialog?.querySelector('.collection-facets__dialog-body');

      if (currentBody && newBody) {
        const scrollTop = currentBody.scrollTop;
        currentBody.innerHTML = newBody.innerHTML;
        currentBody.scrollTop = scrollTop;
      }

      wrapper.dataset.collectionUrl = newFacets.dataset.collectionUrl || this.collectionUrl;
      return true;
    }

    syncActiveFacetsBar(currentInner, newInner) {
      const currentActiveBar = currentInner.querySelector('[data-active-facets]');
      const newActiveBar = newInner.querySelector('[data-active-facets]');

      if (newActiveBar) {
        const replacement = newActiveBar.cloneNode(true);
        if (currentActiveBar) {
          currentActiveBar.replaceWith(replacement);
        } else {
          const summary = currentInner.querySelector('.collection-facets__summary');
          if (summary) {
            summary.after(replacement);
          } else {
            currentInner.prepend(replacement);
          }
        }
        return;
      }

      if (currentActiveBar) {
        currentActiveBar.remove();
      }
    }

    loadMoreProducts(buttonElement) {
      const button = buttonElement || this.loadMoreButton;
      const nextUrl =
        (button && button.dataset.loadNext) ||
        (this.productsContainer && this.productsContainer.dataset.nextUrl);

      if (!nextUrl) {
        this.updateLoadMoreButton('');
        return;
      }

      if (this.loadMoreAbortController) {
        this.loadMoreAbortController.abort();
      }
      this.loadMoreAbortController = new AbortController();

      this.savedScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      this.setLoadMoreLoading(true);

      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      fetch(fetchUrl.toString(), { signal: this.loadMoreAbortController.signal })
        .then(response => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then(html => {
          this.appendProducts(html);
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error('Collection load more error', error);
        })
        .finally(() => {
          this.setLoadMoreLoading(false);
        });
    }

    appendProducts(htmlString) {
      if (!htmlString) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const newSection = doc.querySelector(`${SECTION_SELECTOR}[data-section-id="${this.sectionId}"]`);
      if (!newSection) return;

      const newProductsContainer = newSection.querySelector('[data-products-container]');
      if (!newProductsContainer) return;

      const currentGrid = this.productGrid;
      const currentList = this.productList;
      const newGrid = newProductsContainer.querySelector('[data-product-grid]');
      const newList = newProductsContainer.querySelector('[data-product-list]');
      let appendedCount = 0;
      let scrollTarget = null;

      if (currentGrid && newGrid) {
        const previousLast = currentGrid.lastElementChild;
        appendedCount = this.appendNodes(currentGrid, newGrid.children);
        scrollTarget =
          (previousLast && previousLast.nextElementSibling) || currentGrid.lastElementChild;
      } else if (currentList && newList) {
        const previousLast = currentList.lastElementChild;
        appendedCount = this.appendNodes(currentList, newList.children);
        scrollTarget =
          (previousLast && previousLast.nextElementSibling) || currentList.lastElementChild;
      }

      this.syncProductCount(newSection);

      const nextUrl = newProductsContainer.dataset.nextUrl || '';
      if (this.productsContainer) {
        if (nextUrl) {
          this.productsContainer.dataset.nextUrl = nextUrl;
        } else {
          delete this.productsContainer.dataset.nextUrl;
        }
      }

      this.updateLoadMoreButton(nextUrl);
      this.updateUiState();
      this.restoreScrollPosition();

      document.dispatchEvent(
        new CustomEvent('collection:products-appended', {
          detail: {
            sectionId: this.sectionId,
            appendedCount,
          },
        })
      );
    }

    appendNodes(target, nodes) {
      const fragment = document.createDocumentFragment();
      let count = 0;
      Array.from(nodes).forEach(node => {
        fragment.appendChild(node.cloneNode(true));
        count += 1;
      });
      target.appendChild(fragment);
      return count;
    }

    syncProductCount(sourceSection) {
      if (!sourceSection) return;
      const newCount = sourceSection.querySelector('#ProductCount');
      if (newCount && this.productCountElement) {
        this.productCountElement.innerHTML = newCount.innerHTML;
      }
    }

    restoreScrollPosition() {
      if (this.savedScrollPosition == null) return;
      const y = Math.max(this.savedScrollPosition, 0);
      window.scrollTo(0, y);
      this.savedScrollPosition = null;
    }

    openDialog() {
      const dialog = this.section.querySelector('[data-facets-drawer]');
      if (!dialog) return;

      dialog.classList.remove('is-closing');
      dialog.setAttribute('aria-hidden', 'false');
      dialog.classList.add('is-open');

      const overlay = this.section.querySelector('[data-facets-overlay]');
      if (overlay) {
        overlay.setAttribute('aria-hidden', 'false');
        overlay.classList.add('is-showing');
      }

      const toggleButton = this.section.querySelector('[data-facets-open]');
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true');
      }

      if (typeof trapFocus === 'function') {
        const focusTarget = dialog.querySelector('[data-facet-input], button, a, input, select') || dialog;
        trapFocus(dialog, focusTarget);
      }
    }

    closeDialog() {
      const dialog = this.section.querySelector('[data-facets-drawer]');
      if (!dialog) return;
      if (dialog.classList.contains('is-closing')) return;

      const overlay = this.section.querySelector('[data-facets-overlay]');
      const toggleButton = this.section.querySelector('[data-facets-open]');
      const dialogPanel = dialog.querySelector('.collection-facets__dialog-panel');

      const finishClose = () => {
        dialog.setAttribute('aria-hidden', 'true');
        dialog.classList.remove('is-open', 'is-closing');

        if (overlay) {
          overlay.setAttribute('aria-hidden', 'true');
          overlay.classList.remove('is-showing');
        }

        if (toggleButton) {
          toggleButton.setAttribute('aria-expanded', 'false');
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(toggleButton || null);
        }
      };

      if (!dialog.classList.contains('is-open')) {
        finishClose();
        return;
      }

      dialog.classList.add('is-closing');

      requestAnimationFrame(() => {
        dialog.classList.remove('is-open');

        if (overlay) {
          overlay.classList.remove('is-showing');
        }

        const prefersReducedMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const hasTransition = (() => {
          if (!dialogPanel || prefersReducedMotion) return false;
          const computed = getComputedStyle(dialogPanel);
          return computed.transitionDuration
            .split(',')
            .some(value => parseFloat(value) > 0);
        })();

        if (dialogPanel && hasTransition) {
          const handleTransitionEnd = event => {
            if (event.target !== dialogPanel) return;
            dialogPanel.removeEventListener('transitionend', handleTransitionEnd);
            finishClose();
          };
          dialogPanel.addEventListener('transitionend', handleTransitionEnd);

          // Fallback in case transitionend doesn't fire
          setTimeout(() => {
            if (dialog.classList.contains('is-closing')) {
              finishClose();
            }
          }, 600);
        } else {
          finishClose();
        }
      });
    }

    setLoadingState(isLoading) {
      this.isLoading = isLoading;
      const overlay = this.loadingOverlay;
      if (overlay) {
        overlay.classList.toggle('is-active', isLoading);
        overlay.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
      }
    }

    setLoadMoreLoading(isLoading) {
      const button = this.loadMoreButton;
      if (!button) return;
      button.disabled = isLoading;
      button.classList.toggle('is-loading', isLoading);
      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    updateLoadMoreButton(nextUrl) {
      const button = this.loadMoreButton;
      if (!button) return;
      if (nextUrl) {
        button.dataset.loadNext = nextUrl;
        button.classList.remove('hidden');
        button.disabled = false;
      } else {
        button.classList.add('hidden');
        button.disabled = true;
        button.removeAttribute('aria-busy');
        delete button.dataset.loadNext;
      }
    }

    updateUiState() {
      const countBadge = this.activeCountBadge;
      const clearButton = this.clearButton;
      const activeFacets = this.section.querySelector('[data-active-facets]');
      let appliedCount = 0;

      if (countBadge) {
        const parsedCount = parseInt(countBadge.textContent, 10);
        appliedCount = Number.isNaN(parsedCount) ? 0 : parsedCount;
        countBadge.classList.toggle('is-hidden', appliedCount === 0);
      }

      const hasActiveTags = Boolean(activeFacets);

      if (clearButton) {
        if (appliedCount > 0 || hasActiveTags) {
          clearButton.classList.remove('hidden');
          clearButton.setAttribute('aria-expanded', 'true');
        } else {
          clearButton.classList.add('hidden');
          clearButton.setAttribute('aria-expanded', 'false');
        }
      }

      const loadMoreNext =
        (this.loadMoreButton && this.loadMoreButton.dataset.loadNext) ||
        (this.productsContainer && this.productsContainer.dataset.nextUrl) ||
        '';
      this.updateLoadMoreButton(loadMoreNext);

      const dialog = this.section.querySelector('[data-facets-drawer]');
      if (dialog && dialog.getAttribute('aria-hidden') === 'false' && this.shouldAutoCloseFacets()) {
        this.closeDialog();
      }

      this.syncSortState();
    }

    handleSortSelection(option, selectedValue) {
      const value = selectedValue || option?.dataset.value || '';
      const sortInput = this.sortInput;
      if (!sortInput || !value) return;

      if (sortInput.value === value) return;

      sortInput.value = value;
      this.updateSortOptionsState(value);
      this.submitFacetForms();
    }

    syncSortState() {
      const currentValue = this.getCurrentSortValue();
      this.updateSortOptionsState(currentValue);
    }

    updateSortOptionsState(selectedValue) {
      const options = this.sortOptions;
      if (options && options.length > 0) {
        options.forEach(option => {
          const value = option.dataset.value || '';
          const isActive = value === selectedValue;
          option.classList.toggle('is-active', isActive);
          option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      const sortSelect = this.sortSelect;
      if (sortSelect && sortSelect.value !== selectedValue) {
        sortSelect.value = selectedValue;
      }
    }

    getCurrentSortValue() {
      const sortInput = this.sortInput;
      return sortInput ? sortInput.value || '' : '';
    }

    shouldAutoCloseFacets() {
      const wrapper = this.facetsWrapper;
      if (!wrapper) return true;
      const attr = wrapper.dataset.autoCloseFacets;
      if (attr === undefined) return true;
      return attr === 'true';
    }

    isDrawerOpen() {
      const dialog = this.section.querySelector('[data-facets-drawer]');
      return dialog ? dialog.getAttribute('aria-hidden') === 'false' : false;
    }

    restoreDrawerOpenState() {
      const dialog = this.section.querySelector('[data-facets-drawer]');
      if (!dialog) return;
      const overlay = this.section.querySelector('[data-facets-overlay]');
      const dialogPanel = dialog.querySelector('.collection-facets__dialog-panel');
      const animatedElements = [dialog, overlay, dialogPanel].filter(Boolean);

      this.disableDrawerAnimations(animatedElements);

      if (dialogPanel) {
        dialogPanel.style.transform = 'translateX(0)';
      }

      if (overlay) {
        overlay.style.opacity = '1';
        overlay.style.backdropFilter = 'blur(3px)';
      }

      dialog.classList.remove('is-closing');
      dialog.setAttribute('aria-hidden', 'false');
      dialog.classList.add('is-open');

      if (overlay) {
        overlay.setAttribute('aria-hidden', 'false');
        overlay.classList.add('is-showing');
      }

      const toggleButton = this.section.querySelector('[data-facets-open]');
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true');
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.resetDrawerAnimations(animatedElements);

          if (dialogPanel) {
            dialogPanel.style.removeProperty('transform');
          }

          if (overlay) {
            overlay.style.removeProperty('opacity');
            overlay.style.removeProperty('backdrop-filter');
          }
        });
      });
    }

    disableDrawerAnimations(elements = []) {
      elements.forEach(element => {
        if (!element) return;
        element.dataset.drawerTransitionBackup = element.style.transition || '';
        element.dataset.drawerAnimationBackup = element.style.animation || '';
        element.style.transition = 'none';
        element.style.animation = 'none';
      });
    }

    resetDrawerAnimations(elements = []) {
      requestAnimationFrame(() => {
        elements.forEach(element => {
          if (!element) return;

          if (element.dataset.drawerTransitionBackup !== undefined) {
            const previousTransition = element.dataset.drawerTransitionBackup;
            if (previousTransition) {
              element.style.transition = previousTransition;
            } else {
              element.style.removeProperty('transition');
            }
            delete element.dataset.drawerTransitionBackup;
          } else {
            element.style.removeProperty('transition');
          }

          if (element.dataset.drawerAnimationBackup !== undefined) {
            const previousAnimation = element.dataset.drawerAnimationBackup;
            if (previousAnimation) {
              element.style.animation = previousAnimation;
            } else {
              element.style.removeProperty('animation');
            }
            delete element.dataset.drawerAnimationBackup;
          } else {
            element.style.removeProperty('animation');
          }
        });
      });
    }

    getOpenFacetIds() {
      if (!this.facetsWrapper) return [];
      return Array.from(this.facetsWrapper.querySelectorAll('[data-facet][open]'))
        .map(detail => detail.dataset.id)
        .filter(Boolean);
    }

    syncFacetDrawerState(openFacetIds = [], options = {}) {
      if (!this.facetsWrapper) return;

      const openSet = new Set(openFacetIds);

      this.facetsWrapper.querySelectorAll('[data-facet]').forEach(detailsElement => {
        const summary = detailsElement.querySelector('summary');
        const content = detailsElement.querySelector('[data-drawer-content]');

        detailsElement.removeAttribute('open');
        if (summary) {
          summary.setAttribute('aria-expanded', 'false');
        }
        if (content) {
          content.style.height = '0';
        }
      });

      openFacetIds.forEach(id => {
        const detailsElement = this.facetsWrapper.querySelector(`[data-facet][data-id="${id}"]`);
        if (!detailsElement || !openSet.has(id)) return;

        detailsElement.setAttribute('open', 'true');
        const summary = detailsElement.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
        }

        const host = detailsElement.closest('drawer-disclosure');
        if (host && typeof host.openDrawer === 'function' && !options.skipAnimation) {
          host.openDrawer(detailsElement);
        } else {
          const content = detailsElement.querySelector('[data-drawer-content]');
          if (content) {
            content.style.height = 'auto';
          }
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(section => {
      if (!section.__collectionFacetsController) {
        section.__collectionFacetsController = new CollectionFacetsController(section);
      }
    });
  });
})();
